/**
 * NLP Query Router
 * Routes natural language queries to appropriate analysis functions
 *
 * @module NLPQueryRouter
 * Sprint 32: OOA/OOD Refactoring
 */

import { QueryResponse, InfrastructureReport } from '../types/ii-agent-types';
import { IAnalysisService } from './analysis-service';
import { ResponseFormatter } from '../formatters/response-formatter';

/**
 * Interface for NLP query router
 */
export interface INLPQueryRouter {
    processQuery(query: string): Promise<QueryResponse>;
    extractLocation(query: string): string | null;
}

/**
 * Router for natural language queries
 */
export class NLPQueryRouter implements INLPQueryRouter {
    private analysisService: IAnalysisService;
    private formatter: ResponseFormatter;

    constructor(analysisService: IAnalysisService) {
        this.analysisService = analysisService;
        this.formatter = new ResponseFormatter();
    }

    /**
     * Process natural language query and route to appropriate analysis
     */
    async processQuery(query: string): Promise<QueryResponse> {
        const queryLower = query.toLowerCase();

        try {
            // Check for location-based infrastructure queries
            const locationMatch = this.extractLocation(query);

            // Infrastructure analysis queries
            if (this.matchesInfrastructure(queryLower)) {
                let report: InfrastructureReport;
                if (locationMatch) {
                    report = await this.analysisService.analyzeInfrastructureByLocation(locationMatch);
                } else {
                    report = await this.analysisService.analyzeInfrastructure();
                }

                return {
                    type: 'infrastructure',
                    query: query,
                    response: this.formatter.formatInfrastructure(report, locationMatch),
                    data: report
                };
            }

            // Anomaly detection queries
            if (this.matchesAnomalies(queryLower)) {
                const threshold = 0.5; // Default threshold
                const anomalies = await this.analysisService.detectAnomalies(threshold);
                return {
                    type: 'anomalies',
                    query: query,
                    response: this.formatter.formatAnomalies(anomalies),
                    data: anomalies
                };
            }

            // Security queries
            if (this.matchesSecurity(queryLower)) {
                const findings = await this.analysisService.auditSecurity();
                return {
                    type: 'security',
                    query: query,
                    response: this.formatter.formatSecurity(findings),
                    data: findings
                };
            }

            // Cost queries
            if (this.matchesCost(queryLower)) {
                const recommendations = await this.analysisService.recommendCostOptimizations();
                return {
                    type: 'cost',
                    query: query,
                    response: this.formatter.formatCost(recommendations),
                    data: recommendations
                };
            }

            // Default: run infrastructure analysis
            const report = await this.analysisService.analyzeInfrastructure();
            return {
                type: 'infrastructure',
                query: query,
                response: "I've analyzed your infrastructure. Here's what I found:\n\n" +
                         this.formatter.formatInfrastructure(report),
                data: report
            };

        } catch (error) {
            return {
                type: 'error',
                query: query,
                response: `I encountered an error while processing your query: ${(error as Error).message}`,
                error: error as Error
            };
        }
    }

    /**
     * Extract location from natural language query
     */
    extractLocation(query: string): string | null {
        const queryLower = query.toLowerCase();

        // Common location patterns
        const patterns = [
            /in ([a-z\s]+?)\?/,     // "in Stevenage?"
            /at ([a-z\s]+?)[\s\?]/, // "at Stevenage"
            /from ([a-z\s]+?)[\s\?]/, // "from Stevenage"
            /for ([a-z\s]+?)[\s\?]/,  // "for Stevenage"
        ];

        for (const pattern of patterns) {
            const match = queryLower.match(pattern);
            if (match && match[1]) {
                // Capitalize first letter of each word
                return match[1].trim().split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }
        }

        return null;
    }

    /**
     * Check if query matches infrastructure keywords
     */
    private matchesInfrastructure(query: string): boolean {
        const keywords = ['infrastructure', 'overview', 'how many', 'total', 'servers', 'devices'];
        return keywords.some(kw => query.includes(kw));
    }

    /**
     * Check if query matches anomaly keywords
     */
    private matchesAnomalies(query: string): boolean {
        const keywords = ['anomal', 'outlier', 'unusual', 'different'];
        return keywords.some(kw => query.includes(kw));
    }

    /**
     * Check if query matches security keywords
     */
    private matchesSecurity(query: string): boolean {
        const keywords = ['security', 'vulnerab', 'risk', 'compliance'];
        return keywords.some(kw => query.includes(kw));
    }

    /**
     * Check if query matches cost keywords
     */
    private matchesCost(query: string): boolean {
        const keywords = ['cost', 'save', 'optimize', 'expense'];
        return keywords.some(kw => query.includes(kw));
    }
}
